/* Hash routing (CR-021).
 *
 * Hash, not the History API, for one concrete reason: the server enumerates
 * `client/dist` at startup with `wildcard: false`, so a real path like
 * /build/u-erp-p1-claim-01 would 404 on reload. `#/build/u-erp-p1-claim-01`
 * always serves `/`, so every screen survives a refresh with no server change.
 *
 * This replaces six mutually-exclusive booleans in app.tsx — settingsOpen,
 * drilling, quizUnit, openUnitId, tab, phase — which between them encoded one
 * navigation stack of depth 1, badly: browser Back left the app entirely and
 * nothing was linkable or reload-safe.
 *
 * parse and format are pure and total, so they can be tested without a browser.
 */

export type Route =
  | { name: 'build'; unitId: string | null }
  | { name: 'map'; domainId: string | null }
  | { name: 'reference'; unitId: string | null }
  | { name: 'tutor'; threadId: string | null }
  | { name: 'progress' }
  | { name: 'settings' }
  | { name: 'drill' }
  | { name: 'quiz'; unitId: string };

/** The five surfaces the tab bar offers, in bar order. */
export const TABS = [
  { name: 'build', label: 'Build' },
  { name: 'map', label: 'Map' },
  { name: 'reference', label: 'Reference' },
  { name: 'tutor', label: 'Tutor' },
  { name: 'progress', label: 'Progress' },
] as const;

export const DEFAULT_ROUTE: Route = { name: 'build', unitId: null };

/** Anything unrecognised lands on Build rather than on an error — a bad hash is
 *  a typo or a stale bookmark, not a state the learner should have to escape. */
export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  const [head, tail] = raw.split('/').map((s) => decodeURIComponent(s ?? ''));

  switch (head) {
    case 'build': return { name: 'build', unitId: tail || null };
    case 'map': return { name: 'map', domainId: tail || null };
    case 'reference': return { name: 'reference', unitId: tail || null };
    case 'tutor': return { name: 'tutor', threadId: tail || null };
    case 'progress': return { name: 'progress' };
    case 'settings': return { name: 'settings' };
    case 'drill': return { name: 'drill' };
    // A quiz without a unit id is meaningless; fall through to the default.
    case 'quiz': return tail ? { name: 'quiz', unitId: tail } : DEFAULT_ROUTE;
    default: return DEFAULT_ROUTE;
  }
}

export function formatRoute(route: Route): string {
  const seg = (a: string, b?: string | null) =>
    `#/${a}${b ? `/${encodeURIComponent(b)}` : ''}`;
  switch (route.name) {
    case 'build': return seg('build', route.unitId);
    case 'map': return seg('map', route.domainId);
    case 'reference': return seg('reference', route.unitId);
    case 'tutor': return seg('tutor', route.threadId);
    case 'quiz': return seg('quiz', route.unitId);
    default: return seg(route.name);
  }
}

/** Which tab is lit. Settings, drill and quiz are pushed screens with no tab. */
export function tabOf(route: Route): string | null {
  return (['build', 'map', 'reference', 'tutor', 'progress'] as string[])
    .includes(route.name) ? route.name : null;
}

/** Whether the tab bar belongs on screen at all for this route. */
export function showsTabs(route: Route): boolean {
  return tabOf(route) !== null;
}
